import {
  Template,
  TemplateSavePayload,
  RendererResult,
  RendererPayload,
  TemplateWithData,
  TemplateType,
  Pagination,
} from '@ekkogmbh/apisdk';
import { Add, Delete, Edit, GetApp, Image } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import { createBlobDownload } from 'src/Common/Helper/BlobDownload';
import Permissions from 'src/Common/Permissions';
import DimensionIndicator from '../../Common/Components/DataTable/DimensionIndicator';
import TemplateTypeIndicator from './TemplateTypeIndicator';
import { TemplatePanel } from './TemplatePanel';
import { TemplatePreviewDialog } from './TemplatePreviewDialog';
import React from 'react';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import { Divider, Stack, Typography } from '@mui/material';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useTaskCollections from 'src/Common/Providers/TaskCollectionProvider';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';

// Matches the DimensionIndicator container height so all scrollable
// columns align their row height with the Metadata column.
const ROW_MIN_HEIGHT = 64;

export const TemplateManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { setPanelOpen, isPanelOpen } = useContentControl();
  const { refresh } = usePagination();
  const { scrollTop } = useNavigation();
  const { subscribe: subscribeTaskCollection } = useTaskCollections();

  const [isRendering, startRendering] = useRequest();
  const [, startRequest] = useRequest();

  const [openDialog, setOpenDialog] = useState<'delete' | 'preview'>();
  const [selectedTemplate, setSelectedTemplate] = useState<Template>();

  const dismiss = useCallback(() => {
    setOpenDialog(undefined);
    setPanelOpen(false);
    setSelectedTemplate(undefined);
  }, []);

  const renderTemplate = useCallback(
    (payload: RendererPayload, onAccept?: (result: RendererResult) => void) => {
      startRendering(
        () => api.renderTemplate(payload),
        (result) => {
          if (onAccept) {
            onAccept(result as RendererResult);
          }
        },
      );
    },
    [api],
  );

  const saveTemplate = useCallback(
    async (name: string, coordinate: string, payload: TemplateSavePayload) => {
      startRequest(
        () => api.saveTemplate(coordinate, name, payload, subscribeTaskCollection),
        () => {
          enqueueSnackbar('Templated saved');
          scrollTop();
          dismiss();
          refresh();
        },
      );
    },
    [api],
  );

  const deleteTemplate = useCallback(() => {
    startRequest(
      () => api.deleteTemplate(selectedTemplate!),
      () => {
        enqueueSnackbar('Template deleted');
        dismiss();
        refresh();
      },
    );
  }, [api, selectedTemplate]);

  const showPanel = useCallback((temp: TemplateWithData) => {
    setSelectedTemplate(temp);
    setPanelOpen(true);
    scrollTop();
  }, []);

  const download = useCallback((temp: TemplateWithData) => {
    try {
      const decodedData = Buffer.from(temp.data, 'base64').toString('utf8');
      const blob = new Blob([decodedData], { type: 'text/plain' });
      const filename = `${temp.name}.xsl`;
      createBlobDownload(blob, filename);
    } catch (e) {
      enqueueSnackbar((e as Error).toString(), { variant: 'error' });
    }
  }, []);

  const fetchTemplates = useCallback((pagination: Pagination) => api.getTemplates(pagination), [api]);

  const togglePanel = useCallback(() => {
    setSelectedTemplate(undefined);
    setPanelOpen(!isPanelOpen);
  }, [isPanelOpen]);

  const openPreview = useCallback((entry: TemplateWithData) => {
    setSelectedTemplate(entry);
    setOpenDialog('preview');
  }, []);

  const openDelete = useCallback((entry: TemplateWithData) => {
    setSelectedTemplate(entry);
    setOpenDialog('delete');
  }, []);

  const buttons = useMemo(
    () => [
      <ContentControlButton
        key={'add'}
        content={<Add />}
        onClick={togglePanel}
        tooltip={'Add Template'}
        requiredPermission={Permissions.TEMPLATES_WRITE}
      />,
    ],
    [togglePanel],
  );

  const actions = useMemo(
    () => [
      { title: 'Preview', icon: <Image />, onClick: openPreview },
      { title: 'Download', icon: <GetApp />, onClick: download },
      { title: 'Edit', icon: <Edit />, onClick: showPanel },
      { title: 'Delete', icon: <Delete />, onClick: openDelete },
    ],
    [openPreview, download, showPanel, openDelete],
  );

  const columns = useMemo(
    (): ColumnDef[] => [
      {
        header: 'Name',
        body: (entry: Template, searchText?: string) => (
          <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
            {searchText ? dataTableHighlight(entry.name, [searchText]) : entry.name}
          </ScrollableStack>
        ),
        filterField: 'name',
        sortField: 'T.name',
        proportionalContent: '40%',
      },
      {
        header: 'Coordinate',
        body: (entry: Template, searchText?: string) => (
          <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
            {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
          </ScrollableStack>
        ),
        filterField: 'coordinate',
        sortField: 'N.materializedPath',
        proportionalContent: '60%',
      },
      {
        header: 'Type',
        body: (entry: Template) => <TemplateTypeIndicator type={entry.type} />,
        sortField: 'T.type',
        fitContent: true,
      },
      {
        header: 'Keys',
        body: (entry: Template) => {
          if (entry.type !== TemplateType.APACHE_FOP && entry.type !== TemplateType.SVG_TERA) {
            return '-';
          }

          const tooltipContent = (
            <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 200 }}>
              <Stack spacing={0.5}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  textTransform="uppercase"
                  color="text.secondary"
                  letterSpacing={0.5}
                >
                  Fields ({entry.fields.length})
                </Typography>
                {entry.fields.map((field) => (
                  <Typography key={field} variant="body2" fontFamily="monospace">
                    {field}
                  </Typography>
                ))}
              </Stack>
              {entry.annotations.length > 0 && (
                <>
                  <Divider />
                  <Stack spacing={0.5}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      textTransform="uppercase"
                      color="text.secondary"
                      letterSpacing={0.5}
                    >
                      Annotations ({entry.annotations.length})
                    </Typography>
                    {entry.annotations.map((annotation) => (
                      <Typography key={annotation} variant="body2" fontFamily="monospace">
                        {annotation}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
            </Stack>
          );

          return (
            <TooltipHardened arrow tooltip={tooltipContent} interactive>
              <Stack direction="column" style={{ cursor: 'default' }}>
                <Stack direction="row" spacing={1} style={{ margin: 8 }}>
                  <Typography color="textSecondary" fontWeight={700} noWrap>
                    Fields:
                  </Typography>
                  <Typography color="textPrimary">{entry.fields.length}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} style={{ margin: 8 }}>
                  <Typography color="textSecondary" fontWeight={700} noWrap>
                    Annotations:
                  </Typography>
                  <Typography color="textPrimary">{entry.annotations.length}</Typography>
                </Stack>
              </Stack>
            </TooltipHardened>
          );
        },
        fitContent: true,
      },
      {
        header: 'Metadata',
        body: (entry: Template) => {
          const { width, height } = entry?.metadata ?? {};
          if ([width, height].includes(undefined)) {
            return <></>;
          }
          return <DimensionIndicator widthValue={width} heightValue={height} height={ROW_MIN_HEIGHT} />;
        },
        fitContent: true,
      },
    ],
    [],
  );

  const dialog = useMemo(() => {
    switch (openDialog) {
      case 'delete': {
        const deleteDialogText = (
          <React.Fragment>
            <div>
              name: <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedTemplate?.name}</span>
            </div>
            <div>
              type: <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedTemplate?.type}</span>
            </div>
          </React.Fragment>
        );
        return (
          <GenericDialog
            type="confirmation"
            maxWidth={'sm'}
            fullWidth={true}
            centered={true}
            open={true}
            title={'Delete Template'}
            text={deleteDialogText}
            onClose={dismiss}
            onConfirm={deleteTemplate}
          />
        );
      }
      case 'preview':
        return (
          <TemplatePreviewDialog
            template={selectedTemplate as TemplateWithData}
            renderTemplate={renderTemplate}
            onClose={dismiss}
            isRendering={isRendering}
            dialogProps={{
              open: true,
              fullWidth: true,
              maxWidth: 'lg',
            }}
          />
        );
      default:
        return undefined;
    }
  }, [openDialog, selectedTemplate, isRendering, renderTemplate, deleteTemplate]);

  return (
    <ContentControl
      panel={
        <TemplatePanel
          template={selectedTemplate}
          saveHandler={saveTemplate}
          renderTemplate={renderTemplate}
          closeHandler={dismiss}
          isRendering={isRendering}
        />
      }
      dialog={dialog}
      buttons={buttons}
    >
      <DataTable fetchItems={fetchTemplates} actions={actions} columns={columns} />
    </ContentControl>
  );
};
